import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchUsers, updateUserRole } from '@/features/users/api'
import type { RoleType } from '@/features/users/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'

const ROLE_LABEL: Record<RoleType, string> = { admin: 'Admin', staff: 'Staff', client: 'Client', other: 'Other' }

/**
 * Ports includes/privilege_management.html — assigns a user's coarse role
 * (admin/staff/client) via POST /portal/users/api/update-role/<id>, which
 * already existed as a JSON endpoint before this migration. Distinct from
 * PermissionManagementPage (resource.action grants per role) — this page
 * only decides *which* role a user has.
 */
export default function PrivilegeManagementPage() {
  const queryClient = useQueryClient()
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  })
  const [filter, setFilter] = useState<'all' | RoleType>('all')
  const [editingUserId, setEditingUserId] = useState<string | null>(null)

  const stats = useMemo(() => {
    const counts = { admin: 0, staff: 0, client: 0, other: 0 }
    for (const u of data ?? []) counts[u.role_type]++
    return counts
  }, [data])

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'admin' | 'staff' | 'client' }) =>
      updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUserId(null)
    },
  })

  const filtered = (data ?? []).filter((u) => filter === 'all' || u.role_type === filter)

  if (isLoading) return <LoadingState label="Loading users…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load users.'} onRetry={() => refetch()} />
    )
  }

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold text-ink">Users Privileges</h1>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {(['admin', 'staff', 'client', 'other'] as RoleType[]).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setFilter(filter === r ? 'all' : r)}
            className={`rounded-2xl border p-5 text-left transition-colors ${
              filter === r ? 'border-brand bg-brand text-white' : 'border-nav-line bg-card text-text-primary hover:bg-nav-hover'
            }`}
          >
            <div className="text-2xl font-bold">{stats[r]}</div>
            <div className="text-xs">{ROLE_LABEL[r]}</div>
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
              <th className="px-4 py-3.5 font-semibold">Name</th>
              <th className="px-4 py-3.5 font-semibold">Email</th>
              <th className="px-4 py-3.5 font-semibold">Current Role</th>
              <th className="px-4 py-3.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.user_id} className="border-b border-nav-line last:border-0">
                <td className="px-4 py-3.5 text-ink">
                  {u.firstname} {u.lastname}
                </td>
                <td className="px-4 py-3.5 text-ink">{u.email}</td>
                <td className="px-4 py-3.5 text-ink">{ROLE_LABEL[u.role_type]}</td>
                <td className="px-4 py-3.5">
                  {editingUserId === u.user_id ? (
                    <div className="flex gap-2">
                      {(['admin', 'staff', 'client'] as const).map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => roleMutation.mutate({ userId: u.user_id, role: r })}
                          disabled={roleMutation.isPending}
                          className="rounded-full border border-nav-line px-3 py-1 text-xs hover:bg-brand hover:text-white disabled:opacity-50"
                        >
                          {ROLE_LABEL[r]}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setEditingUserId(null)}
                        className="text-xs text-text-secondary hover:underline"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setEditingUserId(u.user_id)}
                      className="text-xs text-accent hover:underline"
                    >
                      Edit Role
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
